import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useScreenSize } from "hooks";
import classNames from "classnames";
import dayjs from "dayjs";
import { DoughnutChartSmallCard } from "components/molecules/DoughnutChartSmallCard";
import { Typography } from "components/atoms/Typography";
import { Button } from "components/atoms/Button";
import { Modal } from "components/molecules/Modal";
import { ProgressSummaryTable } from "components/molecules/Table/ProgressSummary/ProgressSummaryTable";
import { Carousel } from "components/molecules/Carousel";
import { MOBILE_WIDTH } from "utils/constants";
import { AppealReason } from "../Table/AppealReason/AppealReason";
import { CloseIcon, Spinner } from "components/atoms/Icon";
import { FetchError } from "../FetchErrorMsg";
import { decodeToken } from "utils/functions/decodeToken";
import { getOfficeHoursDisplayName } from "utils/functions/getOfficeHoursDisplayName";
import { matchTestLink } from "utils/functions/matchTestLink";
import { LoadingOverlay } from "../LoadingOverlay";
import "./index.scss";

interface IProps {
  onClick?: any;
  theme: "light" | "dark";
  setIsShowMobileViewAllSummary?: Dispatch<SetStateAction<boolean>>;
  isShowViewAll?: boolean;
  data: any;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
}

export const ViewProgressSummaryCardChart = ({
  onClick,
  theme,
  setIsShowMobileViewAllSummary,
  isShowViewAll,
  data,
  isLoading,
  isError,
  isFetching
}: IProps) => {
  const screen = useScreenSize();
  const [showDetails, setShowDetails] = useState(false);

  const [toggleModal, setToggleModal] = useState(false);

  const payload = decodeToken();

  const testType = payload ? payload.testtype : null;

  const handleViewDetails = () => {
    setShowDetails((prev) => !prev);
  };

  const handleModalShow = () => {
    if (screen.width > MOBILE_WIDTH) {
      setToggleModal((prev) => !prev);
    } else {
      setIsShowMobileViewAllSummary &&
        setIsShowMobileViewAllSummary((prev) => !prev);
    }
  };

  const isRisky = (data: any) => {
    if (
      data &&
      data.DiagnosticsIncompleteCount === 0 &&
      data.AttendanceIncompleteCount === 0 &&
      data.HomeworkIncompleteCount === 0 &&
      data.TestsIncompleteCount === 0 &&
      data.TopicFocusIncompleteCount === 0
    ) {
      return false;
    }
    return true;
  };

  const handleClick = () => {
    const url = matchTestLink(testType);
    window.open(url, "__blank");
  };

  useEffect(() => {
    if (toggleModal === true && screen.width < MOBILE_WIDTH) {
      handleModalShow();
    }
  }, [screen.width]);

  return (
    <div className="progress-summary-card-list-wrapper">
      <div className="progress-summary-header">
        <Typography
          variant="h3Bold"
          label={`${testType} Progress Summary`}
        />
        {isShowViewAll ? 
        <div className="view-all-link" onClick={handleModalShow}>
          <Typography
            variant="h8Underline"
            label="View all"
            styles="link-test-color"
          />
        </div> : null}
      </div>

      <>
        {isLoading ? (
          <Spinner theme={theme} />
        ) : isError ? (
          <FetchError content="Oops! failed to load resources." />
        ) : (
          data && (
            <Carousel renderStyle='progress'>
              {data.ShowDiagnostics && (
                <DoughnutChartSmallCard
                  complete={data.DiagnosticsCompletedCount}
                  inComplete={data.DiagnosticsIncompleteCount}
                  upComing={data.DiagnosticsUpcomingCount}
                  total={data.DiagnosticsCount}
                  theme={theme}
                  onClick={() => onClick("Diagnostics")}
                  title="Diagnostics"
                />
              )}
              {data.ShowAttendance && (
                <DoughnutChartSmallCard
                  complete={data.AttendanceCompletedCount}
                  inComplete={data.AttendanceIncompleteCount}
                  upComing={data.AttendanceUpcomingCount}
                  total={data.AttendanceCount}
                  theme={theme}
                  onClick={() => onClick("Attendance")}
                  title="Attendance"
                />
              )}
              {data.ShowHomework && (
                <DoughnutChartSmallCard
                  complete={data.HomeworkCompletedCount}
                  inComplete={data.HomeworkIncompleteCount}
                  upComing={data.HomeworkUpcomingCount}
                  total={data.HomeworkCount}
                  theme={theme}
                  onClick={() => onClick("Homework")}
                  title="Homework"
                />
              )}
              {data.ShowTests && (
                <DoughnutChartSmallCard
                  complete={data.TestsCompletedCount}
                  inComplete={data.TestsIncompleteCount}
                  upComing={data.TestsUpcomingCount}
                  total={data.TestsCount}
                  theme={theme}
                  onClick={() => onClick("Tests")}
                  title="Tests"
                />
              )}
              {data.ShowTopicFocus && (
                <DoughnutChartSmallCard
                  complete={data.TopicFocusCompletedCount}
                  inComplete={data.TopicFocusIncompleteCount}
                  upComing={data.TopicFocusUpcomingCount}
                  total={data.TopicFocusCount}
                  theme={theme}
                  onClick={() => onClick("TopicFocus")}
                  styles={"border-non"}
                  title={getOfficeHoursDisplayName(testType || "")}
                />
              )}
            </Carousel>
          )
        )}
      </>

      <Modal
        isOpen={toggleModal}
        onClose={() => setToggleModal(false)}
        theme={theme}
        style="progress-summary-view-all-modal"
      >
        <div className={classNames("modal-body-wrapper")}>
          <div className="model-topic">
            <div className="title-wrapper">
              <Typography label="Progress Summary Overview" variant="h4Bold" />
            </div>
            <CloseIcon
              style="progress-summary-view-modal-close"
              onClick={() => setToggleModal(false)}
              theme={theme}
            />
          </div>

          <div className="modal-inner-body-wrapper">
            <div className="progress-summary-sub-title">
              <div className="left-title">
                <Typography label="Baseline Score:&nbsp;" variant="h7Book" />
                {( data.QualifyingScoreStatus === null || data.QualifyingScoreStatus === undefined ) ? (
                  <Typography label="N/A" variant="h7Med" />
                ) : (
                  <Typography
                    label={data.QualifyingScoreStatus}
                    variant="h7Med"
                    styles={
                      data.QualifyingScoreStatus !== "Not Started"
                        ? "accepted-typography-custom-style"
                        : ""
                    }
                  />
                )}
              </div>
              <div className="right-title">
                <Typography label="Target Test Date:&nbsp;" variant="h7Book" />
                {( data.TargetTestDate === null || data.TargetTestDate === undefined ) ? (
                  <Typography label="N/A" variant="h7Bold" />
                ) : Object.keys(data?.TargetTestDate).length > 0 ? (
                  <Typography
                    label={dayjs(data?.TargetTestDate.TestDate).format(
                      "MMMM D, YYYY"
                    )}
                    variant="h7Bold"
                    styles="accepted-typography-custom-style"
                  />
                ) : null}
              </div>
            </div>
            {isLoading ? (
              <Spinner theme={theme} />
            ) : isError ? (
              <FetchError content="Oops! failed to load resources." />
            ) : (
              <>
                <div className="progress-summary-table-wrapper">
                  <ProgressSummaryTable data={data} />
                </div>
                <div className="first-section">
                  <Typography label="Guarantee Eligibility:" variant="h7Bold" />
                  <Typography
                    label={isRisky(data) ? "At Risk" : "No Risk"}
                    variant="h7Med"
                    styles={isRisky(data) ? "warning-text" : "success-text"}
                  />
                  {isRisky(data) ? (
                    <Button
                      icon={false}
                      label="View Details"
                      type="planner"
                      onClick={handleViewDetails}
                      styles="view-detail-btn-custom-style"
                    />
                  ) : null}
                </div>
                {showDetails ? (
                  <LoadingOverlay isLoading={isFetching} theme={theme}>
                    <div className={classNames("appeal-reason-wrapper")}>
                      <AppealReason
                        tableData={data}
                        theme={theme}
                        onParentModalClose={() => setToggleModal(false)}
                      />
                    </div>
                  </LoadingOverlay>
                ) : null}
                <div className="second-section">
                  <Typography
                    label="Want to learn more about our Guarantee Requirements?"
                    variant="h7Med"
                  />
                  <Button
                    icon={false}
                    label="Click here"
                    type="planner"
                    styles="view-detail-btn-custom-style"
                    onClick={handleClick}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};
